import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Layout from '@/components/Layout';
import { NEWS } from '@/lib/index';

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function Events() {
  const featured = NEWS[0];
  const rest = NEWS.slice(1);

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-40 pb-20 min-h-[55vh] flex items-center overflow-hidden bg-[#080808]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1753030768124-dba306907bba?w=1600&q=85"
            alt="Events & News"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(8,8,8,0.4)] to-[#080808]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="eyebrow mb-5">Latest</p>
            <h1 className="heading-display text-[clamp(3rem,7vw,6rem)] text-[#f5f5f5] max-w-3xl">
              News & <span className="text-gold italic">Events</span>
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#080808]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          {/* Featured article */}
          <FadeUp className="mb-16">
            <div className="flex flex-col lg:flex-row gap-0 group cursor-pointer">
              <div className="flex-1 relative overflow-hidden h-80 lg:h-[520px]">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover transition-transform duration-[1s] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[rgba(8,8,8,0.3)] to-transparent" />
                <div className="absolute top-6 left-6">
                  <span className="eyebrow text-[9px] bg-[rgba(8,8,8,0.75)] px-3 py-1.5">Featured</span>
                </div>
              </div>
              <div className="flex-1 bg-[#0c0c0c] border border-[rgba(201,168,76,0.08)] p-10 lg:p-16 flex flex-col justify-center">
                <span className="eyebrow mb-4">{featured.category} · {featured.company}</span>
                <h2 className="heading-display text-3xl lg:text-4xl text-[#f5f5f5] mb-5 group-hover:text-[#c9a84c] transition-colors duration-400">{featured.title}</h2>
                <div className="divider-gold-left" />
                <p className="text-[#f5f5f5]/55 text-sm leading-relaxed mt-5 mb-6">{featured.excerpt}</p>
                <p className="text-[10px] tracking-[0.2em] uppercase text-[#c9a84c]/50">{featured.date}</p>
              </div>
            </div>
          </FadeUp>

          {/* News grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((item, i) => (
              <FadeUp key={item.id} delay={i * 0.1}>
                <article className="group cursor-pointer">
                  <div className="card-editorial h-52 mb-5">
                    <img src={item.image} alt={item.title} loading="lazy" />
                    <div className="card-overlay" />
                    <div className="absolute top-4 left-4">
                      <span className="eyebrow text-[9px] bg-[rgba(8,8,8,0.75)] px-2 py-1">{item.category}</span>
                    </div>
                  </div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#c9a84c]/50 mb-2">{item.date}</p>
                  <h3 className="heading-display text-xl text-[#f5f5f5] leading-snug mb-3 group-hover:text-[#c9a84c] transition-colors duration-300">{item.title}</h3>
                  <p className="text-[#f5f5f5]/45 text-sm leading-relaxed line-clamp-2">{item.excerpt}</p>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-[#c9a84c]/40 mt-3">{item.company}</p>
                  <div className="mt-3 divider-gold-left opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </article>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
